import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { User, Phone, Save, LogOut, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { formatPhoneDisplay } from '../utils/phoneFormat';
import Swal from 'sweetalert2';
import './ProfilePage.css';

const ProfilePage = () => {
  const { member, logout, updateMember } = useAuth();
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    date_of_birth: '',
    gender: '',
    occupation: '',
    health_goal: '',
    allergy_notes: '',
    address: '',
    sub_district: '',
    district: '',
    province: 'เชียงราย',
    postal_code: '57000'
  });

  useEffect(() => {
    if (member) {
      setFormData({
        email: member.email || '',
        date_of_birth: member.date_of_birth || '',
        gender: member.gender || '',
        occupation: member.occupation || '',
        health_goal: member.health_goal || '',
        allergy_notes: member.allergy_notes || '',
        address: member.address || '',
        sub_district: member.sub_district || '',
        district: member.district || '',
        province: member.province || 'เชียงราย',
        postal_code: member.postal_code || '57000'
      });
    }
  }, [member]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    try {
      setIsSaving(true);
      
      const payload = { ...formData };
      if (!payload.date_of_birth) {
        payload.date_of_birth = null;
      }
      
      await updateMember(payload);
      
      Swal.fire({
        title: 'บันทึกสำเร็จ',
        text: 'อัปเดตข้อมูลของคุณเรียบร้อยแล้ว',
        icon: 'success',
        timer: 1500,
        showConfirmButton: false
      });
    } catch (error) {
      console.error('Update profile error:', error);
      Swal.fire({
        title: 'เกิดข้อผิดพลาด',
        text: 'ไม่สามารถบันทึกข้อมูลได้ กรุณาลองใหม่อีกครั้ง',
        icon: 'error',
        confirmButtonColor: '#ef4444'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleLogout = () => {
    Swal.fire({
      title: 'ออกจากระบบ?',
      text: "คุณต้องการออกจากระบบใช่หรือไม่?",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#10b981',
      cancelButtonColor: '#ef4444',
      confirmButtonText: 'ยืนยัน',
      cancelButtonText: 'ยกเลิก'
    }).then((result) => {
      if (result.isConfirmed) {
        logout();
      }
    });
  };

  if (!member) return null;

  return (
    <div className="profile-premium-layout">
      {/* LEFT SIDEBAR: Identity & Status */}
      <aside className="profile-sidebar">
        <motion.div 
          className="identity-card"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        >
          <div className="identity-card-bg"></div>
          
          <div className="avatar-premium-wrapper">
            {member.avatar_url ? (
              <img src={member.avatar_url} alt="Profile" className="avatar-img-premium" />
            ) : (
              <div className="avatar-placeholder-premium">
                <User size={56} strokeWidth={1.5} />
              </div>
            )}
            <div className="avatar-glow"></div>
          </div>
          
          <div className="identity-info">
            <h1 className="member-name-premium">{member.full_name}</h1>
            <div className="member-phone-premium">
              <Phone size={14} className="icon-subtle" />
              <span>{formatPhoneDisplay(member.phone)}</span>
            </div>
            
            <div className="status-divider"></div>
            
            <div className="member-badges-premium">
              <span className={`badge-pill ${member.member_status === 'active' ? 'active' : 'pending'}`}>
                {member.member_status === 'active' ? 'สมาชิกปกติ' : 'รอยืนยัน'}
              </span>
              {member.member_type && member.member_type !== 'retail' && (
                <span className="badge-pill admin">
                  {member.member_type === 'admin' ? 'Admin' : member.member_type}
                </span>
              )}
            </div>
          </div>
        </motion.div>
        
        {/* Floating Save Button inside Sidebar for Desktop */}
        <motion.div 
          className="desktop-save-action"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          <button 
            type="submit" 
            form="profile-form" 
            className="btn-elegant-save" 
            disabled={isSaving}
          >
            {isSaving ? (
              <span className="saving-text">กำลังบันทึก...</span>
            ) : (
              <>
                <span>บันทึกการเปลี่ยนแปลง</span>
                <Save size={18} />
              </>
            )}
          </button>
        </motion.div>
      </aside>

      {/* RIGHT CONTENT: Form Sections */}
      <main className="profile-content">
        <motion.form 
          id="profile-form"
          className="premium-form-layout"
          onSubmit={handleSaveProfile}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
        >
          
          <section className="premium-section">
            <header className="section-header">
              <h2 className="section-heading">ข้อมูลส่วนตัว</h2>
              <p className="section-description">จัดการข้อมูลพื้นฐานและการติดต่อของคุณ</p>
            </header>
            
            <div className="form-glass-card">
              <div className="input-row">
                <div className="input-premium-group full-width">
                  <label>อีเมล</label>
                  <input type="email" name="email" value={formData.email} onChange={handleChange} className="input-elegant" placeholder="อีเมลสำหรับรับข่าวสาร" />
                </div>
              </div>
              <div className="input-row split-2">
                <div className="input-premium-group">
                  <label>วันเกิด</label>
                  <input type="date" name="date_of_birth" value={formData.date_of_birth || ''} onChange={handleChange} className="input-elegant" />
                </div>
                <div className="input-premium-group">
                  <label>เพศ</label>
                  <div className="select-elegant-wrapper">
                    <select name="gender" value={formData.gender} onChange={handleChange} className="select-elegant">
                      <option value="">ไม่ระบุ</option>
                      <option value="male">ชาย</option>
                      <option value="female">หญิง</option>
                      <option value="other">อื่นๆ</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="input-row">
                <div className="input-premium-group full-width">
                  <label>อาชีพ</label>
                  <input type="text" name="occupation" value={formData.occupation} onChange={handleChange} className="input-elegant" placeholder="เช่น พนักงานบริษัท, ธุรกิจส่วนตัว" />
                </div>
              </div>
            </div>
          </section>

          <section className="premium-section">
            <header className="section-header">
              <h2 className="section-heading">ที่อยู่จัดส่ง</h2>
              <p className="section-description">รายละเอียดที่อยู่สำหรับการจัดส่งปิ่นโต</p>
            </header>
            
            <div className="form-glass-card">
              <div className="input-row">
                <div className="input-premium-group full-width">
                  <label>บ้านเลขที่, อาคาร, ซอย, ถนน</label>
                  <textarea 
                    name="address" 
                    value={formData.address} 
                    onChange={handleChange} 
                    className="textarea-elegant"
                    placeholder="กรอกรายละเอียดที่อยู่ให้ครบถ้วน..."
                    rows={2}
                  ></textarea>
                </div>
              </div>
              <div className="input-row split-2">
                <div className="input-premium-group">
                  <label>ตำบล / แขวง</label>
                  <input type="text" name="sub_district" value={formData.sub_district} onChange={handleChange} className="input-elegant" />
                </div>
                <div className="input-premium-group">
                  <label>อำเภอ / เขต</label>
                  <input type="text" name="district" value={formData.district} onChange={handleChange} className="input-elegant" />
                </div>
              </div>
              <div className="input-row split-2">
                <div className="input-premium-group">
                  <label>จังหวัด</label>
                  <input type="text" name="province" value={formData.province} onChange={handleChange} className="input-elegant" />
                </div>
                <div className="input-premium-group">
                  <label>รหัสไปรษณีย์</label>
                  <input type="text" name="postal_code" value={formData.postal_code} onChange={handleChange} className="input-elegant" />
                </div>
              </div>
            </div>
          </section>

          <section className="premium-section">
            <header className="section-header">
              <h2 className="section-heading">สุขภาพและโภชนาการ</h2>
              <p className="section-description">เป้าหมายและข้อจำกัดในการรับประทานอาหาร</p>
            </header>
            
            <div className="form-glass-card">
              <div className="input-row">
                <div className="input-premium-group full-width">
                  <label>เป้าหมายสุขภาพของคุณ</label>
                  <div className="select-elegant-wrapper">
                    <select name="health_goal" value={formData.health_goal} onChange={handleChange} className="select-elegant">
                      <option value="">เลือกเป้าหมาย</option>
                      <option value="ลดน้ำหนัก">ลดน้ำหนัก</option>
                      <option value="สร้างกล้ามเนื้อ">สร้างกล้ามเนื้อ</option>
                      <option value="รักษาสุขภาพ">รักษาสุขภาพ</option>
                      <option value="ควบคุมโรค">ควบคุมโรค (เบาหวาน, ความดัน ฯลฯ)</option>
                      <option value="อื่นๆ">อื่นๆ</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <div className="form-glass-card allergy-glass-card mt-6">
              <div className="allergy-header">
                <div className="allergy-icon">
                  <AlertCircle size={20} />
                </div>
                <div>
                  <h3 className="allergy-title">ข้อมูลการแพ้อาหาร</h3>
                  <p className="allergy-subtitle">โปรดระบุให้ชัดเจนเพื่อความปลอดภัย</p>
                </div>
              </div>
              <div className="input-row mt-4">
                <div className="input-premium-group full-width">
                  <textarea 
                    name="allergy_notes" 
                    value={formData.allergy_notes} 
                    onChange={handleChange} 
                    className="textarea-elegant input-danger-elegant"
                    placeholder="เช่น แพ้กุ้ง, ไม่ทานเนื้อวัว, แพ้ถั่วลิสง... (ปล่อยว่างได้หากไม่มี)"
                    rows={3}
                  ></textarea>
                </div>
              </div>
            </div>
          </section>

        </motion.form>
      </main>

      {/* Floating Save Button for Mobile Only */}
      <div className="mobile-save-action">
        <button 
          type="submit" 
          form="profile-form" 
          className="btn-elegant-save mobile-full" 
          disabled={isSaving}
        >
          {isSaving ? 'กำลังบันทึก...' : <><Save size={20} /> บันทึกข้อมูล</>}
        </button>
      </div>

    </div>
  );
};

export default ProfilePage;
